/**
 * CalendarSettingsContent - Conteúdo de configurações de calendários
 *
 * Componente puro com as 3 abas (Meus calendários, Compartilhamento,
 * Integrações). Reutilizado tanto pela página /tenant/calendar/settings
 * quanto pelo CalendarSettingsModal aberto a partir do calendário.
 *
 * RELEASE:
 *   2026-08-04 v1.2.0 - Removida a aba "Integrações" (opções migraram para o
 *                       modal de criação/edição de calendário)
 *   2026-08-04 v1.1.0 - Coluna "Dono" e ícone do Google em calendários sincronizados
 *   2026-06-24 v1.0.0 - Extraído de CalendarSettings.tsx para reuso em modal

 */

import { useState } from 'react';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';

import {
  Plus,
  Pencil,
  Shield,
  Trash2,
  Star,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCalendars, type UserCalendar } from '@/hooks/useCalendars';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { CalendarFormDialog } from './CalendarFormDialog';
import { CalendarPermissionsMatrix } from './CalendarPermissionsMatrix';


const VISIBILITY_LABELS: Record<UserCalendar['visibility'], string> = {
  private: 'Privado',
  tenant: 'Tenant',
  shared: 'Compartilhado',
};

const PERMISSION_LABELS: Record<UserCalendar['user_permission'], string> = {
  manage: 'Gerenciar',
  edit: 'Editar',
  view: 'Visualizar',
  free_busy: 'Livre/Ocupado',
  none: 'Sem acesso',
};

interface CalendarSettingsContentProps {
  /** Aba inicial; padrão: calendars */
  defaultTab?: 'calendars' | 'sharing';
}


export function CalendarSettingsContent({
  defaultTab = 'calendars',
}: CalendarSettingsContentProps) {
  const { calendars, isLoading, remove, isRemoving } = useCalendars();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserCalendar | null>(null);
  const [permissionsFor, setPermissionsFor] = useState<UserCalendar | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserCalendar | null>(null);

  const handleEdit = (cal: UserCalendar) => {
    setEditing(cal);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="calendars">Meus calendários</TabsTrigger>
          <TabsTrigger value="sharing">Compartilhamento</TabsTrigger>
        </TabsList>


        <TabsContent value="calendars" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {calendars.length} calendário{calendars.length === 1 ? '' : 's'}{' '}
              disponíve{calendars.length === 1 ? 'l' : 'is'}
            </p>
            <Button size="xs" onClick={handleCreate}>
              <Plus className="mr-1.5" /> Novo calendário
            </Button>
          </div>

          <div className="rounded-lg border border-border/50 bg-card">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : calendars.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">
                Nenhum calendário ainda. Crie o primeiro!
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Dono</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Visibilidade</TableHead>
                    <TableHead>Permissão</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calendars.map((cal) => {
                    const canManage = cal.user_permission === 'manage';
                    const isGoogle = !!cal.google_integration_id;
                    // Nome do dono (fallback: e-mail e, por último, UUID truncado)
                    const ownerLabel =
                      (cal.owner_full_name && cal.owner_full_name.trim()) ||
                      (cal.owner_email && cal.owner_email.trim()) ||
                      cal.owner_user_id.slice(0, 8).toUpperCase();
                    return (
                      <TableRow key={cal.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="h-3 w-3 rounded-sm shrink-0 border border-border/50"
                              style={{ backgroundColor: cal.color }}
                              aria-hidden
                            />
                            {isGoogle && (
                              <img
                                src={googleCalendarIcon}
                                alt="Sincronizado com Google Agenda"
                                title="Sincronizado com Google Agenda"
                                className="h-3.5 w-3.5 shrink-0"
                              />
                            )}
                            <span className="truncate font-medium">{cal.name}</span>
                            {cal.is_primary && (
                              <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="truncate" title={cal.owner_email ?? undefined}>
                            {ownerLabel}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {isGoogle ? 'Google' : 'Manual'}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-sm">
                          {VISIBILITY_LABELS[cal.visibility]}
                        </TableCell>
                        <TableCell className="text-sm">
                          {PERMISSION_LABELS[cal.user_permission]}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                          >
                            <StatusDot tone={cal.is_active ? 'emerald' : 'muted'} />
                            {cal.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handleEdit(cal)}
                              title="Editar"
                              disabled={!canManage}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setPermissionsFor(cal)}
                              title="Compartilhamento"
                              disabled={!canManage}
                            >
                              <Shield className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteTarget(cal)}
                              title="Excluir"
                              disabled={!canManage}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="sharing" className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Selecione um calendário para configurar as permissões por perfil. Apenas calendários
            onde você tem permissão de <strong>Gerenciar</strong> aparecem aqui.
          </p>
          <div className="rounded-lg border border-border/50 bg-card divide-y divide-border/50">
            {calendars
              .filter((c) => c.user_permission === 'manage')
              .map((cal) => (
                <button
                  key={cal.id}
                  type="button"
                  onClick={() => setPermissionsFor(cal)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-muted/30 text-left transition-colors"
                >
                  <span
                    className="h-3 w-3 rounded-sm shrink-0 border border-border/50"
                    style={{ backgroundColor: cal.color }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{cal.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Visibilidade: {VISIBILITY_LABELS[cal.visibility]}
                    </p>
                  </div>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            {calendars.filter((c) => c.user_permission === 'manage').length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-10">
                Você não tem permissão de gerenciamento em nenhum calendário ainda.
              </p>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* Diálogos auxiliares */}
      <CalendarFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        calendar={editing}
      />
      <CalendarPermissionsMatrix
        calendar={permissionsFor}
        open={!!permissionsFor}
        onOpenChange={(open) => !open && setPermissionsFor(null)}
      />
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir calendário?</AlertDialogTitle>
            <AlertDialogDescription>
              O calendário <strong>{deleteTarget?.name}</strong> e todos os seus eventos
              manuais serão excluídos permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
              disabled={isRemoving}
            >
              {isRemoving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
